import { CursorController } from "./core/controllers/CursorController";
import { IStringModule } from "./core/IStringModule";
import { EventManager } from "./core/managers/EventManager";
import { ModuleManager } from "./core/managers/ModuleManager";
import { ObjectManager } from "./core/managers/ObjectManager";
import { ScrollManager } from "./core/managers/ScrollManager";
import { StringContext } from "./core/StringContext";
import { StringData } from "./core/StringData";
import { StringModule } from "./core/StringModule";
import { DefaultToolsContainer } from "./core/StringToolsContainer";
import { StringCursor } from "./modules/cursor/StringCursor";
import { StringImpulse } from "./modules/cursor/StringImpulse";
import { StringMagnetic } from "./modules/cursor/StringMagnetic";
import { StringSpotlight } from "./modules/cursor/StringSpotlight";
import { StringLazy } from "./modules/loading/StringLazy";
import { StringLoading } from "./modules/loading/StringLoading";
import { StringInview } from "./modules/screen/StringInview";
import { StringResponsive } from "./modules/screen/StringResponsive";
import { StringAnchor } from "./modules/scroll/StringAnchor";
import { StringGlide } from "./modules/scroll/StringGlide";
import { StringLerp } from "./modules/scroll/StringLerp";
import { StringParallax } from "./modules/scroll/StringParallax";
import { StringProgress } from "./modules/scroll/StringProgress";
import { StringScrollbar } from "./modules/scrollbar/StringScrollbar";
import { StringSplit } from "./modules/text/StringSplit";
import { StringDelayLerpTracker } from "./modules/tracker/StringDelayLerpTracker";
import { StringFPSTracker } from "./modules/tracker/StringFPSTracker";
import { StringLerpTracker } from "./modules/tracker/StringLerpTracker";
import { StringPositionTracker } from "./modules/tracker/StringPositionTracker";
import { StringObject } from "./objects/StringObject";
import { ScrollMode } from "./states/ScrollState";
import { Debounce } from "./utils/Debounce";
import { EventCallback } from "./models/event/EventCallback";
import { StringFPS } from "./utils/StringFPS";
import { StringSettings } from "./utils/StringSettings";
import { StringVideoAutoplay } from "./modules/loading/StringVideoAutoplay";
import { ScrollMarkRule } from "./models/scroll/ScrollTriggerRule";
import { StringSequence } from "./modules/slider/StringSequence";
import { StringForm } from "./modules/input/StringForm";
import { ISettingsChangeData } from "./models/event/ISettingsChangeData";
import { CenterCache } from "./core/managers/CenterCache";
import { HoverTracker } from "./core/managers/HoverTracker";
import { CursorReactiveModule } from "./modules/cursor/CursorReactiveModule";
import { StringScroller } from "./modules/scroll/StringScroller";
import { parsePartOf } from "./utils/ParsePartOf";
import { StringProgressPart } from "./modules/scroll/StringProgressPart";
import { frameDOM } from "./utils/frame-dom";
import { styleTxn } from "./utils/style-txn";

function isTouchDevice() {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}
function isSafari(): boolean {
  let ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf("safari") != -1) {
    if (ua.indexOf("chrome") > -1) {
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
}

class StringTune {
  /** Bound handler for the scroll start event */
  private onScrollStartBind: any;

  /** Bound handler for the scroll stop event */
  private onScrollStopBind: any;

  /** Bound handler for the scroll direction change event */
  private onDirectionChangeBind: any;

  /** Bound wheel event handler */
  private onWheelBind: any;

  /** Bound scroll event handler */
  private onScrollBind: any;

  /** Bound resize event handler */
  private onResizeBind: any;

  /** Bound mouse move handler */
  private onMouseMoveBind: any;

  private onContainerTransitionEndBind: any;
  private onResizeObserverBind: any;
  private pendingScroll: boolean = false;
  private lastScrollEmitted: number = NaN;

  /** Singleton instance of StringTune */
  private static i: StringTune;

  /** Root scrollable element (typically <body>) */
  private root: any;

  /** Window object (used for event bindings and dimensions) */
  private window: any;

  /** Previous window width for resize diff check */
  private prevWidth: number = 0;

  /** Previous window height for resize diff check */
  private prevHeight: number = 0;

  /** Manages all modules registered in the system */
  private moduleManager: ModuleManager;

  /** Manages scroll modes and active scroll engine */
  private scrollManager: ScrollManager;

  /** Manages all interactive objects (elements with `string-*` attributes) */
  private objectManager: ObjectManager;

  /** Central event manager for internal pub-sub logic */
  private eventManager: EventManager;

  /** Handles custom cursor logic (if enabled) */
  private cursorController: CursorController;

  /** Provides default utility tools (parsers, interpolation, etc.) */
  private tools: DefaultToolsContainer;

  /** Main loop used for frame updates (with fixed FPS) */
  private loop: StringFPS = new StringFPS();

  /** Global reactive data store (scroll, viewport, etc.) */
  private data: StringData;

  /** Context shared across all modules (events, data, tools, settings) */
  private context: StringContext;

  /** Caches the center positions of string objects. */
  private centers: CenterCache;

  /** Tracks hover states of string objects. */
  private hoverManager: HoverTracker;

  private observerContainerResize: ResizeObserver | null = null;

  public canRebuild: boolean = true;

  /**
   * Sets the scroll position manually.
   * This overrides all internal scroll states including target and lerped values.
   * Useful for programmatic jumps or syncing scroll externally.
   *
   * @param value The new scroll position in pixels.
   */
  public set scrollPosition(value: number) {
    this.data.scroll.current = value;
    this.data.scroll.target = value;
    this.data.scroll.transformedCurrent =
      this.data.scroll.current * this.data.viewport.transformScale;
    this.data.scroll.delta = 0;
    this.data.scroll.lerped = 0;
    this.scrollManager.updatePosition();
    this.moduleManager.onScroll();
    this.objectManager.checkInview();
  }

  /**
   * Configures the container element(s) used for scroll tracking.
   * Accepts either the `Window` object or an `HTMLElement`.
   * Determines the appropriate internal element references based on the input type
   * and triggers a resize calculation.
   *
   * @param {Window | HTMLElement | any} container The target window or HTML element to associate with scrolling.
   * Handles `Window`, `HTMLElement`, and potentially other types via fallback.
   */
  public set scrollContainer(container: any) {
    this.observerContainerResize?.unobserve(this.context.data.scroll.container);

    this.data.scroll.elementContainer.removeEventListener(
      "transitionend",
      this.onContainerTransitionEndBind
    );
    if (container instanceof Window) {
      this.data.scroll.container = document.body;
      this.data.scroll.elementContainer = document.documentElement;
      this.data.scroll.scrollContainer = container;
    } else if (container instanceof HTMLElement) {
      this.data.scroll.container = container;
      this.data.scroll.elementContainer = container;
      this.data.scroll.scrollContainer = container;
    } else {
      // Fallback case
      this.data.scroll.container = document.body;
      this.data.scroll.elementContainer = document.documentElement;
      this.data.scroll.scrollContainer = container;
    }
    this.data.scroll.elementContainer.addEventListener(
      "transitionend",
      this.onContainerTransitionEndBind
    );
    this.observerContainerResize?.observe(this.context.data.scroll.container);

    this.debouncedResize();
  }

  /**
   * Gets the current scroll position in pixels.
   * This is typically updated every frame.
   */
  public get scrollPosition() {
    return this.data.scroll.current;
  }

  public get scrollHeight() {
    return this.data.viewport.contentHeight;
  }

  public get containerHeight() {
    return this.data.viewport.windowHeight;
  }

  /**
   * Sets the base scroll speed for smooth scrolling.
   * Typically a value between 0 and 1.
   */
  public set speed(value: number) {
    this.data.scroll.speed = value;
  }

  /**
   * Sets the scroll acceleration using a normalized value from 0 to 1.
   * Internally maps it to a real acceleration value between 0.1 and 0.5.
   *
   * @param speed A normalized acceleration factor (0 to 1).
   */
  public set speedAccelerate(speed: number) {
    const min = 0.1;
    const max = 0.5;
    this.data.scroll.speedAccelerate = min + (max - min) * speed;
  }

  /**
   * Sets the scroll mode for desktop devices.
   * Can be 'smooth', 'default', or 'disable'.
   */
  public set scrollDesktopMode(mode: ScrollMode) {
    this.scrollManager.setDesktopMode(mode);
  }

  /**
   * Sets the scroll mode for mobile devices.
   * Can be 'smooth', 'default', or 'disable'.
   */
  public set scrollMobileMode(mode: ScrollMode) {
    this.scrollManager.setMobileMode(mode);
  }

  public set FPSTrackerVisible(visible: boolean) {
    this.data.system.fpsTracker = visible;
    this.eventManager.emit("tracker:fps:visible", visible);
  }

  public set PositionTrackerVisible(visible: boolean) {
    this.data.system.positionTracker = visible;
    this.eventManager.emit("tracker:position:visible", visible);
  }

  public set domBatcherEnabled(enabled: boolean) {
    this.objectManager.setDOMBatcherEnabled(enabled);
  }

  public set intersectionObserverEnabled(enabled: boolean) {
    this.objectManager.setIntersectionObserverEnabled(enabled);
  }

  private debouncedResize = Debounce(this.onResize, 30);

  private constructor() {
    this.root = document.body;
    this.window = window;

    this.tools = new DefaultToolsContainer();
    this.data = new StringData();
    this.eventManager = new EventManager();
    this.moduleManager = new ModuleManager(this.data);
    this.objectManager = new ObjectManager(
      this.data,
      this.moduleManager,
      this.eventManager,
      this.tools
    );

    this.centers = new CenterCache();
    this.hoverManager = new HoverTracker();

    this.context = {
      events: this.eventManager,
      data: this.data,
      tools: this.tools,
      settings: {},
      centers: this.centers,
      hover: this.hoverManager,
    };

    this.cursorController = new CursorController(1, this.context);
    this.scrollManager = new ScrollManager(this.context);

    this.setupSettings({
      "global-class": false,
      "offset-top": "0%",
      "offset-bottom": "0%",
      key: "--progress",
      "inview-top": "0%",
      "inview-bottom": "0%",
      "enter-el": "top",
      "enter-vp": "bottom",
      "exit-el": "bottom",
      "exit-vp": "top",
      "parallax-bias": "0.0",
      parallax: "0.2",
      lerp: "0.2",
      "cursor-lerp": "0.75",
      radius: "150",
      strength: "0.3",
      glide: "1",
      anchor: "center center",
      timeout: 900,
      alignment: "center",
      "target-disable": "false",
      "target-style-disable": "false",
      "target-class": "",
      active: "false",
      fixed: "false",
      repeat: "false",
      "self-disable": "false",
      abs: "false",
      easing: "cubic-bezier(0.25, 0.25, 0.25, 0.25)",
      "glide-base-velocity": 0.00125,
      "glide-reduce-velocity": 0.0000625,
      "glide-negative-velocity": -0.0001,

      "position-strength": 3,
      "position-tension": 0.05,
      "position-friction": 0.15,
      "position-max-velocity": 10,
      "position-update-threshold": 0.1,
      "rotation-strength": 0.75,
      "rotation-tension": 0.06,
      "rotation-friction": 0.18,
      "rotation-max-angular-velocity": 6,
      "rotation-max-angle": 18,
      "rotation-update-threshold": 0.15,
      "max-offset": 220,
      "sleep-epsilon": 0.01,
      "continuous-push": true,
    });

    this.onContainerTransitionEndBind = this.onContainerTransitionEnd.bind(this);

    this.onResizeObserverBind = this.onResizeObserverEvent.bind(this);
    this.observerContainerResize = new ResizeObserver(this.onResizeObserverBind);
    this.observerContainerResize.observe(this.context.data.scroll.container);

    this.onWheelBind = this.onWheelEvent.bind(this);
    this.onScrollBind = this.onScrollEvent.bind(this);
    this.onResizeBind = this.onResize.bind(this);
    this.onMouseMoveBind = this.onMouseMoveEvent.bind(this);

    this.onScrollStartBind = this.onScrollStart.bind(this);
    this.onScrollStopBind = this.onScrollStop.bind(this);
    this.onDirectionChangeBind = this.onDirectionChange.bind(this);

    this.eventManager.on(`wheel`, this.onWheelBind);

    this.scrollManager.bindEvents({
      onScrollStart: this.onScrollStartBind,
      onScrollStop: this.onScrollStopBind,
      onDirectionChange: this.onDirectionChangeBind,
    });

    this.loop.setOnFrame((time: number) => {
      this.data.time.delta = time - this.data.time.now;
      this.data.time.previous = this.data.time.now;
      this.data.time.now = time;
      this.data.time.elapsed += this.data.time.delta;
      this.onUpdateEvent();
    });
    this.on("image:load:all", () => {
      this.onResize();
    });

    this.scrollContainer = window;
  }

  /**
   * Returns the singleton instance of StringTune.
   * If not already created, initializes it.
   */
  public static getInstance(): StringTune {
    if (!StringTune.i) {
      StringTune.i = new StringTune();
    }
    return StringTune.i;
  }

  /**
   * Finds and returns an existing module by its class.
   * Useful for reusing a module instance without re-registering.
   *
   * @template T The type of the module to retrieve.
   * @param type The module class constructor.
   * @returns The module instance if found, otherwise undefined.
   */
  public reuse<T>(type: new (...args: any[]) => T): T | undefined {
    return this.moduleManager.find(type);
  }

  /**
   * Instantiates and registers a new module.
   * Accepts optional per-instance settings that override global settings.
   *
   * @param objectClass The module class to instantiate.
   * @param settings Optional settings specific to this module.
   */
  public use(objectClass: typeof StringModule, settings: any = null) {
    const effectiveSettings = {
      ...this.context.settings,
      ...settings,
    };
    const module = new objectClass({
      events: this.eventManager,
      data: this.data,
      tools: this.tools,
      settings: effectiveSettings,
      centers: this.centers,
      hover: this.hoverManager,
    });
    this.moduleManager.register(module);
  }

  /**
   * Subscribes to a global event within the system.
   *
   * @param eventName The name of the event to listen for.
   * @param callback The function to call when the event is triggered.
   * @param id Optional subscription ID (for easier management).
   */
  public on(eventName: string, callback: EventCallback<any>, id: string = "") {
    this.eventManager.on(eventName, callback, id);
  }
  public emit(eventName: string, data: any) {
    this.eventManager.emit(eventName, data);
  }

  /**
   * Unsubscribes from a global event.
   *
   * @param eventName The name of the event.
   * @param callback The previously registered callback.
   * @param id Optional ID used during subscription.
   */
  public off(eventName: string, callback: EventCallback<any>, id: string = "") {
    this.eventManager.off(eventName, callback, id);
  }

  /**
   * Adds a scroll trigger rule that activates when the user scrolls past a defined offset
   * in a specific direction. This can be used to toggle CSS classes or execute callbacks
   * when elements come into view or go out of view.
   *
   * @param rule - The scroll trigger configuration object.
   *   - `id`: A unique identifier for this rule.
   *   - `offset`: The vertical scroll offset (in pixels) where the rule should activate.
   *   - `direction`: Defines the scroll direction required to activate the rule.
   *                 Can be `"forward"`, `"backward"`, or `"any"`.
   *   - `onEnter`: (Optional) A function that will be called when the scroll position enters the trigger zone
   *                in the specified direction.
   *   - `onLeave`: (Optional) A function that will be called when the scroll position leaves the trigger zone
   *                or scrolls in the opposite direction.
   *   - `toggleClass`: (Optional) An object defining a class toggle behavior.
   *                    It contains a target element and a class name to be added when the trigger is active
   *                    and removed when it's not.
   */
  public addScrollMark(rule: ScrollMarkRule) {
    this.scrollManager.addScrollMark(rule);
  }

  /**
   * Removes a scroll trigger by its unique identifier.
   *
   * @param id - The unique identifier of the scroll trigger to be removed.
   */
  public removeScrollMark(id: string) {
    this.scrollManager.removeScrollMark(id);
  }

  /**
   * Starts the scroll engine and initializes all listeners, observers, and modules.
   *
   * @param fps Desired frames per second for the update loop.
   */
  public start(fps: number) {
    this.data.scroll.scrollContainer?.addEventListener("scroll", this.onScrollBind);
    this.data.scroll.container?.addEventListener("wheel", this.onWheelBind, {
      passive: false,
    });

    window.addEventListener("resize", this.onResizeBind);
    this.root.addEventListener("mousemove", this.onMouseMoveBind);

    const observerContainerMutation = new MutationObserver(
      (mutationsList: MutationRecord[], observer: MutationObserver) => {
        for (const mutation of mutationsList) {
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "style" || mutation.attributeName === "class")
          ) {
            this.onResize();
          }
        }
      }
    );
    const config: MutationObserverInit = {
      attributes: true,
      attributeFilter: ["style", "class"],
    };
    observerContainerMutation.observe(this.context.data.scroll.container, config);

    this.use(StringInview);

    const htmlFontSize = window.getComputedStyle(document.documentElement).fontSize;
    const fontSizeNumber = parseFloat(htmlFontSize);
    this.context.data.viewport.baseRem = fontSizeNumber;

    document.documentElement.classList.add("-string");
    this.moduleManager.onInit();
    this.onResize();
    this.initObjects();
    this.objectManager.observeDOM();

    this.loop.start(fps);
    this.eventManager.emit(`start`, null);
  }

  /**
   * Initializes all DOM elements with `string` or `string-copy-from` attributes.
   * Registers them with the object manager and triggers resize/scroll/frame hooks.
   */
  private initObjects() {
    document.querySelectorAll("[string],[data-string]").forEach((element) => {
      this.objectManager.add(element as HTMLElement);
    });
    document.querySelectorAll("[string-copy-from],[data-string-copy-from]").forEach((element) => {
      let connectTargetId = this.tools.domAttribute.process({
        element: element as HTMLElement,
        key: "copy-from",
        fallback: "",
      });

      if (connectTargetId && connectTargetId.length > 0) {
        this.objectManager.linkMirror(connectTargetId, element as HTMLElement);
      }
    });
    // document.querySelectorAll("[string-part-of],[data-string-part-of]").forEach((element) => {
    //   let partOfTargetId = this.tools.domAttribute.process({
    //     element: element as HTMLElement,
    //     key: "part-of",
    //     fallback: "",
    //   });
    //   let partOfData = parsePartOf(partOfTargetId as string);
    //   if (partOfData) {
    //     if (partOfData.id && partOfData.id.length > 0) {
    //       if (this.objectManager.all.has(partOfData.id)) {
    //         let partOfObject = new StringObject("", element as HTMLElement);
    //         partOfObject.setProperty("part-of-id", partOfData.id);
    //         partOfObject.setProperty("start", partOfData.start);
    //         partOfObject.setProperty("end", partOfData.end);

    //         this.objectManager.all.get(partOfData.id)!.partOf.push(partOfObject);
    //       } else {
    //         this.objectManager.enqueuePartOf(partOfData.id, element as HTMLElement);
    //       }
    //     }
    //   }
    // });
    this.moduleManager.onResize();
    this.moduleManager.onScroll();
    this.moduleManager.onFrame();
  }

  /**
   * Sets global fallback settings for all modules.
   * These can be overridden by module-specific settings during `use(...)`.
   *
   * @param settings A key-value map of default settings (e.g. 'offset-top': '-10%').
   */
  public setupSettings(settings: StringSettings): void {
    this.context.settings = {
      ...this.context.settings,
      ...settings,
    };
    this.onSettingsChange({
      isDesktop: this.data.viewport.windowWidth > 1024,
      widthChanged: true,
      heightChanged: true,
      scrollHeightChanged: true,
      isForceRebuild: false,
    });
  }

  private onResizeObserverEvent() {
    this.debouncedResize();
  }

  private onContainerTransitionEnd(event: TransitionEvent) {
    if (event.target === this.context.data.scroll.container) {
      this.onResize();
      this.moduleManager.onResize();
      this.moduleManager.onScroll();
      this.moduleManager.onFrame();
    }
  }

  /**
   * Handles mouse move event and dispatches it to cursor and modules.
   * @param e Native mouse move event.
   */
  private onMouseMoveEvent(e: MouseEvent) {
    this.cursorController.onMouseMove(e);
    this.moduleManager.onMouseMove(e);
    frameDOM.measure(() => {
      this.moduleManager.onMouseMoveMeasure();
    });
  }

  /**
   * Handles wheel scroll event and passes it to the scroll engine and modules.
   * @param e Native wheel event.
   */
  private onWheelEvent(e: WheelEvent) {
    const target = e.target as HTMLElement;
    const isModal = target.closest("[string-isolation],[data-string-isolation]");
    if (isModal != null) return;

    this.scrollManager.get().onWheel(e);
    this.moduleManager.onWheel(e);
  }

  /**
   * Called when scrolling begins.
   * Triggers module scroll start lifecycle hook.
   */
  private onScrollStart() {
    this.moduleManager.onScrollStart();
    this.eventManager.emit(`scroll:start`, null);
  }

  /**
   * Called when scrolling ends.
   * Triggers module scroll stop lifecycle hook.
   */
  private onScrollStop() {
    this.moduleManager.onScrollStop();
    this.eventManager.emit(`scroll:stop`, null);
  }

  /**
   * Called when scrolling ends.
   * Triggers module scroll stop lifecycle hook.
   */
  private onDirectionChange() {
    this.moduleManager.onDirectionChange();
  }

  /**
   * Called when global or module settings are updated.
   * Notifies all managers and modules to re-read new settings.
   */
  private onSettingsChange(data: ISettingsChangeData) {
    this.cursorController.onSettingsChange(data);
    this.objectManager.onSettingsChange(data);
    this.moduleManager.onSettingsChange(data);
  }

  /**
   * Handles native scroll event.
   * Prevents default behavior and triggers internal scroll logic and event emissions.
   *
   * @param e The native scroll event.
   */
  private onScrollEvent(e: Event) {
    e.preventDefault();

    this.context.centers.invalidateAll();

    this.scrollManager.get().onScroll(e);
    this.pendingScroll = true;
    return false;
  }

  /**
   * Called every frame by the update loop.
   * Triggers scroll engine, modules, and global `update` event.
   */
  private onUpdateEvent() {
    this.cursorController.onFrame();
    this.scrollManager.get().onFrame();
    this.moduleManager.onFrame();

    if (this.pendingScroll || this.data.scroll.current !== this.lastScrollEmitted) {
      this.pendingScroll = false;
      this.moduleManager.onScroll();
      this.objectManager.checkInview();
      this.eventManager.emit(`lerp`, this.data.scroll.lerped);
      this.eventManager.emit(`scroll`, this.data.scroll.current);
      frameDOM.measure(() => {
        this.moduleManager.onScrollMeasure();
      });
      this.lastScrollEmitted = this.data.scroll.current;
    }

    frameDOM.mutate(() => {
      styleTxn.begin();
      this.moduleManager.onMutate();
      styleTxn.commit();
    });

    this.eventManager.emit(`update`, null);
  }

  /**
   * Handles resize events from scroll container or window.
   * Ignores height-only changes on mobile to prevent layout jumps.
   * Rebuilds layout and triggers module resize if size really changed.
   */
  public onResize(force: boolean = false): void {
    if (this.canRebuild == false) {
      return;
    }

    const container = this.data.scroll.container;
    const scroll = this.context.data.scroll;
    let width = 0;
    let height = 0;
    var newScrollHeight;
    var newContainerTopPosition = 0;
    const rect = container.getBoundingClientRect();

    if (container.tagName == "BODY") {
      width = window.innerWidth;
      height = window.innerHeight;
    } else {
      width = rect.width;
      height = rect.height;
    }

    newContainerTopPosition = rect.top;
    newScrollHeight = scroll.container.scrollHeight;
    const transformScale = this.tools.transformScaleParser.process({
      value: window.getComputedStyle(container).transform,
    });
    this.context.data.viewport.transformScale =
      window.getComputedStyle(container).scale == "none"
        ? transformScale
        : Number(window.getComputedStyle(container).scale);
    this.context.data.scroll.transformedCurrent =
      this.context.data.scroll.current * this.context.data.viewport.transformScale;
    const isDesktop = width > 1024;

    const widthChanged = this.prevWidth !== width;
    const heightChanged = this.prevHeight !== height;
    const scrollHeightChanged = this.context.data.viewport.contentHeight !== newScrollHeight;

    const shouldRebuild = widthChanged || (isDesktop && heightChanged) || scrollHeightChanged;

    this.context.data.scroll.topPosition = Math.floor(newContainerTopPosition);
    this.context.data.viewport.contentWidth = width;
    this.context.data.viewport.contentHeight = newScrollHeight;

    this.prevWidth = width;
    this.prevHeight = height;

    this.context.data.viewport.windowWidth = width;
    this.context.data.viewport.windowHeight = height;

    const htmlFontSize = window.getComputedStyle(document.documentElement).fontSize;
    const fontSizeNumber = parseFloat(htmlFontSize);
    this.context.data.viewport.baseRem = fontSizeNumber * transformScale;

    scroll.bottomPosition = this.context.data.viewport.contentHeight - height;

    if (widthChanged || (typeof force === "boolean" && force)) {
      this.moduleManager.onResizeWidth();
    }
    if (shouldRebuild || (typeof force === "boolean" && force)) {
      if (this.context.data.scroll.container.scrollTop > 0) {
        this.context.data.scroll.current = this.context.data.scroll.container.scrollTop;
        this.context.data.scroll.target = this.context.data.scroll.container.scrollTop;
      }

      this.scrollManager.updateResponsiveMode();
      this.moduleManager.onResize();
      this.onSettingsChange({
        isDesktop,
        widthChanged,
        heightChanged,
        scrollHeightChanged,
        isForceRebuild: force === true,
      });
      this.objectManager.invalidateInviewIndex();
      this.moduleManager.onScroll();
      this.moduleManager.onScrollMeasure();
      this.moduleManager.onFrame();
      this.objectManager.checkInview();
    }
  }

  /**
   * Scrolls the container to the specified element identified by a CSS selector, applying an optional offset.
   *
   * Calculates the vertical position of the target element relative to the scroll container and updates the scroll delta accordingly.
   * If the element is not found, a warning is logged to the console.
   *
   * @param selector - The CSS selector string used to identify the target element.
   * @param offset - Optional. The number of pixels to offset from the target element's top position. Defaults to 0.
   */
  public scrollToElement(selector: string, offset: number = 0) {
    const element = document.querySelector(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollTop =
        rect.top + window.pageYOffset - this.data.scroll.container.clientTop - offset;
      this.context.data.scroll.delta = scrollTop - this.data.scroll.current;
    } else {
      console.warn(`Element not found: ${selector}`);
    }
  }

  public scrollTo(position: number) {
    this.scrollManager.get().scrollTo(position);
  }

  public invalidateCenter(id: string): void {
    this.centers.invalidate(id);
  }

  /**
   * Forces center cache recalculation for all tracked objects.
   * Useful when DOM geometry changes outside of StringTune's control.
   */
  public invalidateCenters(): void {
    this.centers.invalidateAll();
  }

  /**
   * Cleans up the system, removes all event listeners, stops the loop,
   * and destroys modules and event subscriptions.
   */
  public destroy() {
    this.data.scroll.scrollContainer?.removeEventListener("scroll", this.onScrollBind);
    this.data.scroll.container?.removeEventListener("wheel", this.onWheelBind);
    this.data.scroll.elementContainer.removeEventListener(
      "transitionend",
      this.onContainerTransitionEndBind
    );
    this.window.removeEventListener("resize", this.onResizeBind);
    this.root.removeEventListener("mousemove", this.onMouseMoveBind);
    this.loop.stop();
    this.moduleManager.destroy();
    this.eventManager.clearAll();
    this.eventManager.off(`wheel`, this.onWheelBind);
  }
}

export {
  StringTune as default,
  StringCursor,
  StringDelayLerpTracker,
  StringFPSTracker,
  StringGlide,
  StringLazy,
  StringLerp,
  StringLerpTracker,
  StringLoading,
  StringForm,
  StringImpulse,
  StringMagnetic,
  StringParallax,
  StringPositionTracker,
  StringProgress,
  StringResponsive,
  StringSpotlight,
  StringScrollbar,
  StringSplit,
  StringAnchor,
  StringTune as StringTune,
  StringVideoAutoplay,
  StringModule,
  StringObject,
  StringData,
  StringSequence,
  StringScroller,
  StringProgressPart,
  CursorReactiveModule,
  frameDOM,
  styleTxn,
  type ScrollMarkRule as ScrollTriggerRule,
  type StringContext,
};

// Export performance utilities
export { DOMBatcher } from "./core/managers/DOMBatcher";
